import {
  BaseAvatarIcon,
  DropdownSelect,
  FaroText,
  FilterMenuOptionType,
  grey,
  NoTranslate,
  type UserDisplayInfo,
} from "@faro-lotv/flat-ui";
import { useTranslate } from "@faro-lotv/foreign-observers";
import {
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { UserTeamAvatar } from "../../users-invite/user-team-avater";
import { dropDownStyle } from "./annotation-editor-meta-fields";
import {
  SearchableDropdownProps,
  SearchableListContent,
  SearchableListContentProps,
} from "./searchable-dropdown";
import { useIsAnnotationEditorDark } from "./annotation-editor-surface-context";

function UssignedTeamAvatar(): JSX.Element {
  const isDark = useIsAnnotationEditorDark();
  const translate = useTranslate();
  return (
    <UserTeamAvatar
      userDisplayInfo={{
        name: translate("Unassigned"),
        email: "",
      }}
      icon={<BaseAvatarIcon />}
      size="xs"
      shouldShowWhiteRim={false}
      displayOption="nameOnly"
      isTeam={false}
      dark={isDark}
    />
  );
}

/** menu entry for the case when user is unassigned */
const unassignedUserOption: SearchableListContentProps = {
  key: "unassigned",
  value: "",
  searchText: "",
  label: <UssignedTeamAvatar />,
};

/**
 * @returns The content of searchable dropdown specialized for list of users
 */
export function SearchableUsersDropdown({
  selectedOption,
  options,
  onOptionChange,
  disabled,
}: SearchableDropdownProps): JSX.Element {
  const isDark = useIsAnnotationEditorDark();
  /** Assign dropdown options that also contain an option to unassign */
  const fullAssigneeOptions = useMemo(
    () => [
      unassignedUserOption,
      ...options.map((o) => ({
        ...o,
        label: <NoTranslate>{o.label}</NoTranslate>,
        searchText: isValidElement<{ userDisplayInfo: UserDisplayInfo }>(
          o.label,
        )
          ? o.label.props.userDisplayInfo.name +
            o.label.props.userDisplayInfo.email
          : "",
      })),
    ],
    [options],
  );

  const [userOption, setUserOption] = useState(unassignedUserOption);

  useEffect(() => {
    const option = fullAssigneeOptions.find((o) => o.key === selectedOption);
    if (option?.key !== userOption.key) {
      setUserOption(option?.value.length ? option : unassignedUserOption);
    }
  }, [selectedOption, fullAssigneeOptions, userOption.key]);

  const updateUser = useCallback(
    (option: FilterMenuOptionType) => {
      if (option.key !== selectedOption) {
        setUserOption(option);
        onOptionChange(option.value.length ? option.key : undefined);
      }
    },
    [selectedOption, onOptionChange],
  );

  return (
    <DropdownSelect
      value={userOption.value}
      dark={isDark}
      disabled={disabled}
      label="Assignee"
      sx={{ mt: "3px", height: "38px", ...dropDownStyle }}
      renderValue={() => (
        <FaroText
          variant="bodyM"
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: isDark ? grey[100] : "text.primary",
          }}
        >
          {userOption.label}
        </FaroText>
      )}
    >
      <SearchableListContent
        disabled={disabled}
        optionKey={userOption.key}
        onSelectOption={updateUser}
        options={fullAssigneeOptions}
      />
    </DropdownSelect>
  );
}
